import clsx from 'clsx'
import { type ReactElement, useState } from 'react'
import { DateInput, Dropdown, InputCol } from '~/components/Form'
import {
  input,
  inputField,
  inputHasLeadingText,
  inputHasTrailingDropdown,
  inputLeadingText,
  inputSmall
} from '~/components/Form/Input/TextInput.css'
import { ButtonTabGroup } from '~/components/Global/Interactions/ButtonTabGroup/ButtonTabGroup'
import {
  formatRelativeDateValue,
  isRelativeDateValue,
  parseRelativeDate,
  type RelativeDateUnit
} from '~/utils/date'
import {
  dateRangeEditorPanelHidden,
  dateRangeEditorTabs,
  dateRangeEditorWrapper,
  dateRangeRelativeNumberInput
} from '../FilterBar.css'
import type { DateRangeFilterDef, FiltersState } from '../types'

type DateRangeEditorProps = {
  definition: DateRangeFilterDef
  filters: FiltersState
  onChange: (filters: FiltersState) => void
}

type Mode = 'fixed' | 'relative'

const modeOptions = [
  { label: 'Dates fixes', value: 'fixed' as Mode },
  { label: 'Dates relatives', value: 'relative' as Mode }
]

const unitOptions: { value: RelativeDateUnit; label: string }[] = [
  { value: 'hour', label: 'heures' },
  { value: 'day', label: 'jours' },
  { value: 'week', label: 'semaines' },
  { value: 'month', label: 'mois' },
  { value: 'year', label: 'années' }
]

const toUnit = (value: string): RelativeDateUnit =>
  unitOptions.find((option) => option.value === value)?.value ?? 'month'

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const toFixedDate = (value?: string): Date | null => {
  if (!value || isRelativeDateValue(value)) return null
  // Date-only strings must be parsed as local midnight to avoid UTC offset shift
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const date = new Date(dateOnly ? `${value}T00:00:00` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const DateRangeEditor = ({
  definition,
  filters,
  onChange
}: DateRangeEditorProps): ReactElement => {
  const afterValue = filters[definition.keyAfter]
  const beforeValue = filters[definition.keyBefore]

  const afterRel = afterValue ? parseRelativeDate(afterValue) : null
  const beforeRel = beforeValue ? parseRelativeDate(beforeValue) : null

  const [mode, setMode] = useState<Mode>(
    afterRel || beforeRel ? 'relative' : 'fixed'
  )
  const [afterCount, setAfterCount] = useState(
    afterRel ? String(afterRel.count) : ''
  )
  const [afterUnit, setAfterUnit] = useState<RelativeDateUnit>(
    afterRel?.unit ?? 'month'
  )
  const [beforeCount, setBeforeCount] = useState(
    beforeRel ? String(beforeRel.count) : ''
  )
  const [beforeUnit, setBeforeUnit] = useState<RelativeDateUnit>(
    beforeRel?.unit ?? 'month'
  )

  const handleFixedChange = (key: string, date: Date | null) => {
    if (!date) {
      const current = filters[key]
      if (!current || isRelativeDateValue(current)) return
      const newFilters = { ...filters }
      delete newFilters[key]
      onChange(newFilters)
      return
    }
    onChange({ ...filters, [key]: toLocalDateString(date) })
  }

  const handleRelativeChange = (
    key: string,
    count: string,
    unit: RelativeDateUnit
  ) => {
    const parsed = Number(count)
    if (count !== '' && Number.isFinite(parsed) && parsed > 0) {
      onChange({ ...filters, [key]: formatRelativeDateValue(parsed, unit) })
      return
    }
    const current = filters[key]
    if (!current || !isRelativeDateValue(current)) return
    const newFilters = { ...filters }
    delete newFilters[key]
    onChange(newFilters)
  }

  const handleModeChange = (next: Mode) => {
    if (next === mode) return
    setMode(next)
    if (next === 'relative') {
      setAfterCount(afterRel ? String(afterRel.count) : '')
      setBeforeCount(beforeRel ? String(beforeRel.count) : '')
      setAfterUnit(afterRel?.unit ?? 'month')
      setBeforeUnit(beforeRel?.unit ?? 'month')
    }
  }

  const renderRelativeBound = (
    label: string,
    key: string,
    count: string,
    setCount: (value: string) => void,
    unit: RelativeDateUnit,
    setUnit: (unit: RelativeDateUnit) => void
  ): ReactElement => (
    <div
      className={clsx(
        inputField,
        inputHasLeadingText,
        inputHasTrailingDropdown
      )}
    >
      <div className={inputLeadingText}>{label}</div>
      <input
        type="number"
        min={1}
        placeholder="1"
        value={count}
        className={clsx(input, inputSmall, dateRangeRelativeNumberInput)}
        onChange={(e) => {
          const next = e.currentTarget.value
          setCount(next)
          handleRelativeChange(key, next, unit)
        }}
      />
      <Dropdown
        key={`${key}-${unit}`}
        narrow
        options={unitOptions}
        defaultValue={unit}
        required
        onChange={(value) => {
          const nextUnit = toUnit(value)
          setUnit(nextUnit)
          handleRelativeChange(key, count, nextUnit)
        }}
      />
    </div>
  )

  return (
    <div className={dateRangeEditorWrapper}>
      <ButtonTabGroup
        className={dateRangeEditorTabs}
        options={modeOptions}
        value={mode}
        onChange={handleModeChange}
      />

      <div
        className={mode === 'fixed' ? undefined : dateRangeEditorPanelHidden}
      >
        <InputCol>
          <DateInput
            wrapper={false}
            leadingText="Du"
            placeholder="Sélectionner"
            defaultDate={toFixedDate(afterValue)}
            onChange={(date) => handleFixedChange(definition.keyAfter, date)}
            dateFormatOptions={{ dateStyle: 'short' }}
          />
          <DateInput
            wrapper={false}
            leadingText="Au"
            placeholder="Sélectionner"
            defaultDate={toFixedDate(beforeValue)}
            onChange={(date) => handleFixedChange(definition.keyBefore, date)}
            dateFormatOptions={{ dateStyle: 'short' }}
          />
        </InputCol>
      </div>
      <div
        className={mode === 'relative' ? undefined : dateRangeEditorPanelHidden}
      >
        <InputCol>
          {renderRelativeBound(
            'Depuis',
            definition.keyAfter,
            afterCount,
            setAfterCount,
            afterUnit,
            setAfterUnit
          )}
          {renderRelativeBound(
            "Jusqu'à",
            definition.keyBefore,
            beforeCount,
            setBeforeCount,
            beforeUnit,
            setBeforeUnit
          )}
        </InputCol>
      </div>
    </div>
  )
}
